import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { PILLARS, PILLAR_IDS, PILLAR_BY_ID } from "../data/matrices";
import { useStudio } from "../store/useStudio";
import { colors, fonts, shadows } from "../theme";
import { InfoTip } from "./InfoTip";

const SIZE = 300;
const CENTER = SIZE / 2;
const STROKE = 20;
const GAP = 9;
const RMAX = (SIZE - STROKE) / 2;
const STEP = STROKE + GAP;
const GAP_DEG = 2.2; // transparent gap between segments

// Polar → cartesian with the 0%-at-top, clockwise convention.
const pt = (r, deg) => {
  const a = (-90 + deg) * (Math.PI / 180);
  return [CENTER + r * Math.cos(a), CENTER + r * Math.sin(a)];
};

// Annular sector (donut segment) path between two radii and two angles (degrees).
function annularSector(rOuter, rInner, a0, a1) {
  if (a1 - a0 <= 0.01) return "";
  const large = a1 - a0 > 180 ? 1 : 0;
  const [x0o, y0o] = pt(rOuter, a0);
  const [x1o, y1o] = pt(rOuter, a1);
  const [x1i, y1i] = pt(rInner, a1);
  const [x0i, y0i] = pt(rInner, a0);
  return `M ${x0o} ${y0o} A ${rOuter} ${rOuter} 0 ${large} 1 ${x1o} ${y1o} L ${x1i} ${y1i} A ${rInner} ${rInner} 0 ${large} 0 ${x0i} ${y0i} Z`;
}

// Build the per-block anchored segments (Detailed mode): every block owns one angular slice
// shared across the Systems / Locations / Crops rings. `fill` (0..1) scales the TOTAL swept
// angle to the ring's true percentage of its 100% baseline — so a partial allocation renders
// at its real share instead of auto-filling the whole ring.
function detailedSegments(blocks, fill = 1) {
  const total = blocks.reduce((s, b) => s + (Number(b.area) || 0), 0);
  if (total <= 0) return [];
  const f = Math.max(0, Math.min(1, fill));
  const gaps = GAP_DEG * blocks.length;
  const usable = Math.max(0, (360 - gaps) * f);
  let cursor = 0;
  return blocks.map((b) => {
    const share = (Number(b.area) || 0) / total;
    const span = share * usable;
    const seg = { id: b.id, block: b, share, startDeg: cursor, endDeg: cursor + span };
    cursor += span + GAP_DEG;
    return seg;
  });
}

// Global mode: pool identical sub-items per ring (independent angles, no cross-ring anchoring).
// `fill` (0..1) scales the total swept angle to the ring's true % of its baseline.
function pooledSegments(blocks, kb, dimension, fill = 1) {
  const groups = {};
  blocks.forEach((b) => {
    if (dimension === "crops") {
      const allocs = b.allocations && Object.keys(b.allocations).length ? b.allocations : { _unassigned: 1 };
      Object.entries(allocs).forEach(([cid, share]) => {
        const area = (Number(b.area) || 0) * (Number(share) || 1 / Object.keys(allocs).length);
        groups[cid] = (groups[cid] || 0) + area;
      });
    } else {
      const key = dimension === "systems" ? b.systemId : b.locationId;
      if (!key) return;
      groups[key] = (groups[key] || 0) + (Number(b.area) || 0);
    }
  });
  const entries = Object.entries(groups).filter(([, a]) => a > 0);
  const total = entries.reduce((s, [, a]) => s + a, 0);
  if (total <= 0) return [];
  const f = Math.max(0, Math.min(1, fill));
  const gaps = GAP_DEG * entries.length;
  const usable = Math.max(0, (360 - gaps) * f);
  let cursor = 0;
  return entries.map(([key, area]) => {
    const share = area / total;
    const span = share * usable;
    const seg = { id: `${dimension}-${key}`, key, area, share, startDeg: cursor, endDeg: cursor + span };
    cursor += span + GAP_DEG;
    return seg;
  });
}

export default function RingsOrbit({ progress, rawProgress, funnel, counts, rootPillar, onSelectRoot, kpis }) {
  const order = [rootPillar, ...PILLAR_IDS.filter((id) => id !== rootPillar)];
  const root = PILLAR_BY_ID[rootPillar];
  const rootRealized = progress[rootPillar] || 0;
  const rootPct = Math.round(rootRealized * 100);
  const rootRaw = (rawProgress?.[rootPillar] ?? rootRealized) || 0;
  const rootRawPct = Math.round(rootRaw * 100);
  const clamped = rootRawPct > 100;

  const svgRef = useRef(null);
  const dragPrevRef = useRef(null);
  const rotStartRef = useRef(null);
  const targets = useStudio((s) => s.targets);
  const setTarget = useStudio((s) => s.setTarget);
  const opportunity = useStudio((s) => s.opportunity);
  const setTargetMode = useStudio((s) => s.setTargetMode);
  const targetMode = useStudio((s) => s.targetMode);
  const systemBlocks = useStudio((s) => s.systemBlocks);
  const selections = useStudio((s) => s.selections);
  const kb = useStudio((s) => s.kb);
  const mode = targetMode[rootPillar] || null;
  const target = targets[rootPillar];
  const hasTarget = target != null;

  const [viewMode, setViewMode] = useState("detailed");
  const [hover, setHover] = useState(null);   // { id, pillarId, seg, x, y }
  const [notchInfo, setNotchInfo] = useState(false);
  const [rot, setRot] = useState(0);

  const k = kpis || {};
  const fn = funnel || {};
  const va = fn.viableArea || 0, aa = fn.allocatedArea || 0, sf = fn.systemFootprint || 0;
  // 3-tier funnel wording on the rings (replaces the old "utilized / intensity" copy).
  const RING_METRIC = {
    locations: va > 0 ? `${Math.round(aa).toLocaleString()} / ${Math.round(va).toLocaleString()} m² allocated` : "draw an allocation area",
    systems: aa > 0 ? `${Math.round(sf).toLocaleString()} / ${Math.round(aa).toLocaleString()} m² footprint` : "draw a system footprint",
    crops: `${Math.round(k.total_yield || 0).toLocaleString()} kg yield`,
    resources: `${Math.round(k.water_recovery || 0)}% recovery`,
  };
  // Tier name each ring represents in the funnel (shown in the center + legend).
  const TIER_LABEL = { locations: "Allocation Area", systems: "System Footprint", crops: "Crops", resources: "Resources" };
  const metricLine = RING_METRIC[rootPillar];

  const blocks = useMemo(() => systemBlocks || [], [systemBlocks]);

  // Which crop pillar segments to draw per ring for a given anchored block (Detailed).
  const hasCrops = (b) => b.allocations && Object.keys(b.allocations).length > 0;
  // Per-ring fill fraction (0..1) — the ring's realized % of its 100% baseline.
  const ringFill = (pid) => Math.max(0, Math.min(1, progress[pid] || 0));

  const dimOf = (pillarId) => (pillarId === "systems" ? "systems" : pillarId === "locations" ? "locations" : pillarId === "crops" ? "crops" : null);
  const labelFor = (dim, key) => {
    const arr = dim === "systems" ? kb?.systems : dim === "locations" ? kb?.locations : dim === "crops" ? kb?.crops : null;
    if (key === "_unassigned") return "Unassigned";
    return (arr || []).find((x) => x.id === key)?.label || key;
  };
  // Build the specific data points shown in the hover tooltip + scroll drilldown for a segment.
  const segInfo = (pillarId, seg) => {
    if (seg.block) {
      const b = seg.block;
      const alloc = b.allocatedArea != null && b.allocatedArea > 0 ? b.allocatedArea : b.area;
      const over = b.area > alloc + 0.5;
      const rows = [
        ["Location", b.locationLabel || b.locationId || "—"],
        ["System footprint", `${Math.round(b.area || 0).toLocaleString()} m²`],
        ["Allocated zone", `${Math.round(alloc || 0).toLocaleString()} m²${over ? " ⚠ over" : ""}`],
        ["Share of drafted", `${Math.round((seg.share || 0) * 100)}%`],
        ["Height", b.height ? `${b.height} m` : "—"],
        ["Volume", b.volume ? `${Math.round(b.volume).toLocaleString()} m³` : "—"],
      ];
      const crops = Object.keys(b.allocations || {});
      if (crops.length) rows.push(["Crops", crops.map((c) => labelFor("crops", c)).join(", ")]);
      if (b.overrides) rows.push(["Overrides", "custom baselines set"]);
      return { title: b.systemLabel || b.systemId, rows };
    }
    const dim = dimOf(pillarId);
    return {
      title: labelFor(dim, seg.key),
      rows: [
        ["Pooled area", `${Math.round(seg.area || 0).toLocaleString()} m²`],
        ["Share of loop", `${Math.round((seg.share || 0) * 100)}%`],
      ],
    };
  };

  const targetMet = hasTarget && rootRealized >= target - 0.005;
  const angleRad = (t) => (-90 + t * 360) * (Math.PI / 180);
  const notchX = hasTarget ? CENTER + RMAX * Math.cos(angleRad(target)) : 0;
  const notchY = hasTarget ? CENTER + RMAX * Math.sin(angleRad(target)) : 0;

  const dragTo = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const ang = Math.atan2(e.clientY - cy, e.clientX - cx) * (180 / Math.PI);
    let t = (ang + 90) / 360;
    t = ((t % 1) + 1) % 1;
    const prev = dragPrevRef.current;
    if (prev != null) {
      if (t - prev > 0.5) t = 0;
      else if (prev - t > 0.5) t = 1;
    }
    t = Math.max(0, Math.min(1, t));
    dragPrevRef.current = t;
    setTarget(rootPillar, Math.round(t * 100) / 100);
  }, [rootPillar, setTarget]);

  const startNotchDrag = useCallback((e) => {
    e.stopPropagation();
    dragPrevRef.current = target ?? null;
    let switched = false;
    // First actual movement takes the ring into "manual" (a user-set override of the auto ceiling).
    const move = (ev) => { if (!switched) { switched = true; setTargetMode(rootPillar, "manual"); } dragTo(ev); };
    const up = () => { dragPrevRef.current = null; window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }, [dragTo, target, rootPillar, setTargetMode]);

  // Rotate the sunburst by dragging the ring group.
  const startRotate = useCallback((e) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const startAng = Math.atan2(e.clientY - cy, e.clientX - cx) * (180 / Math.PI);
    rotStartRef.current = { startAng, rot };
    const move = (ev) => {
      const a = Math.atan2(ev.clientY - cy, ev.clientX - cx) * (180 / Math.PI);
      setRot(rotStartRef.current.rot + (a - rotStartRef.current.startAng));
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }, [rot]);

  const autoCeiling = rootPillar === "locations" && opportunity?.notch_pct != null
    ? opportunity.notch_pct
    : Math.min(0.95, Math.max(0.5, rootRealized || 0.6));

  // Default target behaviour is "auto" (ceiling from the Dynamic Opportunity formula). Dragging the
  // notch flips the ring to "manual". The old Auto / Manual / Explore buttons were removed.
  useEffect(() => {
    if (mode !== "auto" && mode !== "manual") setTargetMode(rootPillar, "auto");
  }, [mode, rootPillar, setTargetMode]);
  useEffect(() => {
    if (mode !== "auto") return;
    const t = Math.round(autoCeiling * 100) / 100;
    if (target == null || Math.abs(target - t) > 0.001) setTarget(rootPillar, t);
  }, [mode, autoCeiling, target, rootPillar, setTarget]);

  // Linked brushing — when a segment is hovered, resolve the FULL set of segment ids that are its
  // dependencies across the other rings (so they stay lit while everything unrelated fades).
  //  · Detailed mode: every ring draws one slice per BLOCK (seg.id === block id) → the linked set is
  //    simply that block's id, shared across all four rings.
  //  · Global mode: rings pool by dimension key (seg.id === `${dim}-${key}`) → resolve the blocks
  //    matching the hovered key, then light their system / location / crop pooled slices.
  const linkedIds = useMemo(() => {
    if (!hover?.seg) return null;
    if (viewMode === "detailed") return new Set([hover.seg.id]);
    const hoveredDim = dimOf(hover.pillarId);
    const key = hover.seg.key;
    const matches = (b) => {
      if (hoveredDim === "systems") return b.systemId === key;
      if (hoveredDim === "locations") return b.locationId === key;
      if (hoveredDim === "crops") {
        const has = b.allocations && Object.keys(b.allocations).length;
        return key === "_unassigned" ? !has : !!(b.allocations && b.allocations[key]);
      }
      return false;
    };
    const set = new Set([hover.seg.id]);
    blocks.filter(matches).forEach((b) => {
      if (b.systemId) set.add(`systems-${b.systemId}`);
      if (b.locationId) set.add(`locations-${b.locationId}`);
      const cropKeys = b.allocations && Object.keys(b.allocations).length ? Object.keys(b.allocations) : ["_unassigned"];
      cropKeys.forEach((cid) => set.add(`crops-${cid}`));
    });
    return set;
  }, [hover, viewMode, blocks]);
  const brushing = linkedIds != null;

  // Segments to draw on a given ring (pillar) at radius r.
  const renderRing = (pillarId, r) => {
    const rOuter = r + STROKE / 2;
    const rInner = r - STROKE / 2;
    // Guidance: once drafting has begun, gently pulse rings that are still empty to steer the user.
    const guiding = blocks.length > 0 && ringFill(pillarId) === 0;
    const track = <circle key={`t-${pillarId}`} cx={CENTER} cy={CENTER} r={r} fill="none"
      stroke="rgba(0,0,0,0.05)" strokeWidth={STROKE}
      className={guiding ? "ring-guide-pulse" : undefined}
      data-testid={guiding ? `ring-guide-${pillarId}` : undefined} />;

    let segs;
    if (viewMode === "global") {
      // Independent pooling per ring dimension, scaled to that ring's fill.
      const dim = dimOf(pillarId);
      segs = dim ? pooledSegments(blocks, kb, dim, ringFill(pillarId)) : [];
    } else {
      // Detailed: per-block slices scaled to the ring's true % of its baseline.
      segs = detailedSegments(blocks, ringFill(pillarId));
    }

    const paths = segs.map((seg) => {
      // Baseline fade (no hover): dim the crop ring where a block has no crop assigned, and the
      // resource ring when no resources are selected — these are "empty" placeholder slices.
      let faded = false;
      if (viewMode === "detailed" && pillarId === "crops" && !hasCrops(seg.block)) faded = true;
      if (viewMode === "detailed" && pillarId === "resources") faded = (selections?.resources || []).length === 0;
      const linked = brushing && linkedIds.has(seg.id);
      const opacity = brushing ? (linked ? 1 : 0.14) : (faded ? 0.18 : 1);
      return (
        <path
          key={`${pillarId}-${seg.id}`}
          d={annularSector(rOuter, rInner, seg.startDeg, seg.endDeg)}
          fill={`url(#og-${pillarId})`}
          opacity={opacity}
          stroke={linked ? "#1D1D1F" : "#fff"}
          strokeWidth={linked ? 1.1 : 0.75}
          style={{ cursor: "pointer", transition: "opacity 0.18s ease, stroke-width 0.18s ease" }}
          data-testid={`sunburst-seg-${pillarId}-${seg.id}`}
          onPointerEnter={(e) => setHover({ id: seg.id, pillarId, seg, x: e.clientX, y: e.clientY })}
          onPointerMove={(e) => setHover((h) => (h && h.id === seg.id ? { ...h, x: e.clientX, y: e.clientY } : h))}
          onPointerLeave={() => setHover((h) => (h && h.id === seg.id ? null : h))}
          onClick={(e) => { e.stopPropagation(); onSelectRoot(pillarId); }}
        />
      );
    });

    return <g key={pillarId}>{track}{paths}</g>;
  };

  const hasSegments = blocks.length > 0;

  return (
    <div style={styles.wrap} data-testid="rings-orbit">
      <style>{`@keyframes ringGuidePulse{0%,100%{stroke:rgba(0,0,0,0.04)}50%{stroke:rgba(0,0,0,0.13)}}.ring-guide-pulse{animation:ringGuidePulse 1.8s ease-in-out infinite}`}</style>
      {/* View controls */}
      <div style={styles.controls}>
        <div style={styles.viewToggle} data-testid="ring-view-toggle">
          {[["detailed", "Detailed"], ["global", "Global"]].map(([id, label]) => (
            <button key={id} type="button" data-testid={`view-mode-${id}`} onClick={() => setViewMode(id)}
              style={{ ...styles.viewBtn, ...(viewMode === id ? { background: root.solid, color: "#fff" } : {}) }}>{label}</button>
          ))}
          <span style={{ marginLeft: 4, display: "inline-flex" }}>
            <InfoTip
              testId="view-toggle-info"
              title="Global vs. Detailed"
              body="Detailed focuses on the active loop alone, showing its individual options, footprints, and allocations. Global displays the totals for every loop at once, providing an overview of the complete site balance."
              placement="bottom"
            />
          </span>
        </div>
      </div>

      <div style={{ position: "relative", width: SIZE, height: SIZE }}>
        <svg ref={svgRef} width={SIZE} height={SIZE} style={{ overflow: "visible", touchAction: "none" }}>
          <defs>
            {PILLARS.map((p) => (
              <linearGradient key={p.id} id={`og-${p.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={p.grad[0]} />
                <stop offset="100%" stopColor={p.grad[1]} />
              </linearGradient>
            ))}
          </defs>

          {/* Rotatable ring group (scroll no longer scales — see onWheel drilldown) */}
          <g
            transform={`rotate(${rot} ${CENTER} ${CENTER})`}
            onPointerDown={startRotate}
            data-testid="sunburst-group"
            style={{ cursor: "grab" }}
          >
            {order.map((id, i) => (hasSegments ? renderRing(id, RMAX - i * STEP)
              : <circle key={id} cx={CENTER} cy={CENTER} r={RMAX - i * STEP} fill="none" stroke="rgba(0,0,0,0.05)" strokeWidth={STROKE} />))}
          </g>

          {/* Ecological ceiling + draggable target notch (on the outer/root ring) */}
          <circle cx={CENTER} cy={CENTER} r={RMAX} fill="none" stroke={root.solid} strokeWidth={1.5} strokeDasharray="2 5" opacity={0.4} data-testid="ring-ghost-track" />
          {hasTarget && (
            <g data-testid="ring-target-notch" style={{ cursor: "grab" }} onPointerDown={startNotchDrag}
              onPointerEnter={() => setNotchInfo(true)} onPointerLeave={() => setNotchInfo(false)}>
              <line
                x1={CENTER + (RMAX - STROKE / 2 - 3) * Math.cos(angleRad(target))}
                y1={CENTER + (RMAX - STROKE / 2 - 3) * Math.sin(angleRad(target))}
                x2={CENTER + (RMAX + STROKE / 2 + 3) * Math.cos(angleRad(target))}
                y2={CENTER + (RMAX + STROKE / 2 + 3) * Math.sin(angleRad(target))}
                stroke={targetMet ? colors.ok : "#1D1D1F"} strokeWidth={2.5} strokeLinecap="round"
              />
              <circle cx={notchX} cy={notchY} r={8} fill="#fff" stroke={targetMet ? colors.ok : "#1D1D1F"} strokeWidth={2.5} />
            </g>
          )}
        </svg>

        {notchInfo && hasTarget && (
          <div
            data-testid="auto-ceiling-tip"
            style={{ position: "absolute", left: notchX, top: notchY, transform: "translate(-50%, calc(-100% - 12px))", zIndex: 40, width: 210, padding: "8px 10px", borderRadius: 10, background: "rgba(29,29,31,0.94)", color: "#fff", fontFamily: fonts.sans, fontSize: 10.5, lineHeight: 1.4, textAlign: "center", boxShadow: "0 8px 24px rgba(0,0,0,0.25)", pointerEvents: "none" }}
          >
            {mode === "manual"
              ? `Manual target. Drag to adjust. Auto ceiling ${Math.round(autoCeiling * 100)}%.`
              : `Ceiling set by the Dynamic Opportunity formula (${Math.round(autoCeiling * 100)}%). Drag to set your own target.`}
          </div>
        )}
        <div style={{ ...styles.center, background: "radial-gradient(circle, rgba(255,255,255,0.96) 50%, rgba(255,255,255,0) 72%)" }} data-testid="rings-center">
          <div style={{ ...styles.pct, color: root.solid }}>{rootPct}<span style={styles.pctSign}>%</span></div>
          {clamped && (
            <div style={styles.rawChip} data-testid="rings-raw-pct" title="Clamped to 100%; true value shown">raw {rootRawPct}%</div>
          )}
          <div style={styles.rootLabel}>
            {TIER_LABEL[rootPillar] || root.label}
            {!hasSegments && <span style={{ pointerEvents: "auto" }}><InfoTip testId="rings-empty-info" title="Empty loop" body="No drafted instances yet. Draft a zone and system pairing to populate this loop and activate its metrics." placement="bottom" /></span>}
          </div>
          <div style={styles.metricLine} data-testid="rings-metric-line">{metricLine}</div>
          {hasTarget && (
            <div style={{ ...styles.targetChip, color: targetMet ? colors.ok : colors.textSecondary }} data-testid="rings-target-chip">
              {targetMet ? "✓ " : "◦ "}Target {Math.round(target * 100)}%
            </div>
          )}
        </div>
      </div>

      {hasSegments && (
        <div style={styles.startHint} data-testid="rings-start-hint">
          <span style={styles.startDot} />
          Drag to rotate. Hover a segment to view its data.
        </div>
      )}

      <div style={styles.legend}>
        {["locations", "systems", "crops", "resources"].map((pid) => {
          const p = PILLAR_BY_ID[pid];
          const isRoot = rootPillar === p.id;
          const n = counts[p.id] || 0;
          return (
            <button key={p.id} data-testid={`ring-legend-${p.id}`} onClick={() => onSelectRoot(p.id)}
              style={{ ...styles.legendItem, background: isRoot ? "#fff" : "transparent", boxShadow: isRoot ? "0 1px 6px rgba(0,0,0,0.08)" : "none", borderColor: isRoot ? p.solid : "transparent" }}>
              <span style={{ ...styles.dot, background: `linear-gradient(135deg, ${p.grad[0]}, ${p.grad[1]})` }} />
              <span style={{ ...styles.legendText, color: isRoot ? colors.textPrimary : colors.textSecondary }}>{p.label}</span>
              <span style={{ ...styles.count, color: n ? p.solid : colors.textTertiary }}>{n}</span>
            </button>
          );
        })}
      </div>

      {/* Instant hover tooltip at the cursor — portalled to <body> so it isn't clipped or offset by
          any transformed/scrolled ancestor, and positioned exactly where the mouse is (like the KPI
          info cards). Clamped to stay on-screen. */}
      {hover?.seg && typeof document !== "undefined" && createPortal(
        (() => {
          const info = segInfo(hover.pillarId, hover.seg);
          const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
          const vh = typeof window !== "undefined" ? window.innerHeight : 800;
          const estH = 44 + info.rows.length * 20;
          const left = Math.max(8, Math.min(hover.x + 16, vw - 244));
          const top = Math.max(8, Math.min(hover.y + 16, vh - estH - 8));
          return (
            <div style={{ ...styles.tooltip, left, top }} data-testid="sunburst-tooltip">
              <div style={styles.tipTitle}>{info.title}</div>
              {info.rows.map(([l, v]) => (
                <div key={l} style={styles.tipRow}><span style={styles.tipLabel}>{l}</span><span style={styles.tipVal}>{v}</span></div>
              ))}
            </div>
          );
        })(),
        document.body
      )}
    </div>
  );
}

const styles = {
  wrap: { display: "flex", flexDirection: "column", alignItems: "center" },
  tooltip: { position: "fixed", zIndex: 60, minWidth: 150, maxWidth: 230, padding: "9px 11px", borderRadius: 12, background: "rgba(29,29,31,0.94)", backdropFilter: "blur(10px)", boxShadow: "0 8px 24px rgba(0,0,0,0.25)", pointerEvents: "none" },
  tipTitle: { fontFamily: fonts.sans, fontSize: 12.5, fontWeight: 700, color: "#fff", marginBottom: 5, letterSpacing: -0.2 },
  tipRow: { display: "flex", justifyContent: "space-between", gap: 12, fontFamily: fonts.sans, fontSize: 11 },
  tipLabel: { color: "rgba(255,255,255,0.6)" },
  tipVal: { color: "#fff", fontWeight: 600 },
  tipHint: { marginTop: 6, fontFamily: fonts.mono, fontSize: 8.5, letterSpacing: 0.6, textTransform: "uppercase", color: "rgba(255,255,255,0.45)" },
  drilldown: { marginTop: 14, width: "100%", padding: "12px 14px", borderRadius: 14, background: "#fff", border: `1px solid ${colors.border}`, boxShadow: shadows.soft },
  drillHead: { display: "flex", alignItems: "center", gap: 8, marginBottom: 8 },
  drillDot: { width: 9, height: 9, borderRadius: 999, flexShrink: 0 },
  drillTitle: { fontFamily: fonts.sans, fontSize: 13, fontWeight: 700, color: colors.textPrimary, letterSpacing: -0.2 },
  drillRing: { fontFamily: fonts.mono, fontSize: 9, letterSpacing: 0.8, color: colors.textTertiary, textTransform: "uppercase" },
  drillClose: { marginLeft: "auto", width: 22, height: 22, borderRadius: 999, border: "none", background: "rgba(0,0,0,0.05)", color: colors.textSecondary, fontSize: 11, cursor: "pointer" },
  drillRow: { display: "flex", justifyContent: "space-between", gap: 12, padding: "4px 0", borderTop: `1px solid ${colors.border}`, fontFamily: fonts.sans, fontSize: 11.5 },
  drillLabel: { color: colors.textSecondary },
  drillVal: { color: colors.textPrimary, fontWeight: 600, textAlign: "right" },
  controls: { width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 },
  viewToggle: { display: "flex", gap: 2, padding: 3, borderRadius: 999, background: "rgba(0,0,0,0.05)" },
  viewBtn: { padding: "6px 14px", borderRadius: 999, border: "none", background: "transparent", fontFamily: fonts.sans, fontSize: 11.5, fontWeight: 700, color: colors.textSecondary, cursor: "pointer", transition: "background 0.18s, color 0.18s" },
  center: { position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" },
  pct: { fontFamily: fonts.sans, fontSize: 42, fontWeight: 700, letterSpacing: -1.5, lineHeight: 1, display: "flex", alignItems: "baseline" },
  pctSign: { fontSize: 17, fontWeight: 600, marginLeft: 2, opacity: 0.8 },
  rawChip: { fontFamily: fonts.mono, fontSize: 8.5, letterSpacing: 0.5, fontWeight: 700, color: "#8a5a00", background: "rgba(255,159,10,0.16)", borderRadius: 7, padding: "1px 6px", marginTop: 3 },
  rootLabel: { fontFamily: fonts.sans, fontSize: 12, fontWeight: 600, color: colors.textPrimary, marginTop: 6 },
  metricLine: { fontFamily: fonts.mono, fontSize: 9.5, letterSpacing: 0.3, color: colors.textSecondary, marginTop: 4, maxWidth: 150, textAlign: "center" },
  targetChip: { fontFamily: fonts.mono, fontSize: 9.5, letterSpacing: 0.6, fontWeight: 700, marginTop: 4 },
  pathwaysWrap: { width: "100%", display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 14 },
  pathways: { display: "flex", gap: 6 },
  pathBtn: { padding: "7px 14px", borderRadius: 999, borderWidth: 1, borderStyle: "solid", borderColor: colors.border, background: "#fff", fontFamily: fonts.sans, fontSize: 11.5, fontWeight: 600, color: colors.textSecondary, cursor: "pointer", boxShadow: shadows.soft, transition: "background 0.18s, color 0.18s, border-color 0.18s" },
  pathHint: { marginTop: 8, maxWidth: 240, textAlign: "center", fontFamily: fonts.sans, fontSize: 10.5, lineHeight: 1.4, color: colors.textTertiary },
  blueSkyBtn: { marginTop: 8, padding: "8px 16px", borderRadius: 999, border: "none", background: colors.brand, fontFamily: fonts.sans, fontSize: 12, fontWeight: 700, color: "#fff", cursor: "pointer", boxShadow: "0 2px 8px rgba(81,170,254,0.32)" },
  legend: { marginTop: 18, width: "100%", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
  startHint: { marginTop: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, fontFamily: fonts.sans, fontSize: 11, fontWeight: 500, color: colors.textSecondary, textAlign: "center" },
  startDot: { width: 7, height: 7, borderRadius: 999, background: "linear-gradient(135deg, #498DCC, #212E20)", display: "inline-block", flexShrink: 0 },
  legendItem: { display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 12, borderWidth: 1, borderStyle: "solid", borderColor: "transparent", cursor: "pointer", transition: "background 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease" },
  dot: { width: 10, height: 10, borderRadius: 999, flexShrink: 0 },
  legendText: { fontFamily: fonts.sans, fontSize: 12, fontWeight: 600 },
  count: { marginLeft: "auto", fontFamily: fonts.mono, fontSize: 12, fontWeight: 700, minWidth: 14, textAlign: "right" },
};
